import React from 'react'
import { useTranslation } from 'react-i18next'

import { capitalizeFirstLetter } from '@/common/utils/capitalizeFirstLetter'
import { getDisplayName } from '@/common/utils/getDisplayName'
import i18n from '@/lib/i18n'
import { useModalProvider } from './useModalProvider'

export function useLanguageSync() {
  const { t } = useTranslation('common')
  const { handleModal } = useModalProvider()

  const normalizeLocale = (locale: string): string => {
    const mapping: { [key: string]: string } = {
      'pt-BR': 'pt',
      en: 'en',
      'en-US': 'en',
      es: 'es',
      'es-ES': 'es',
      it: 'it',
      'it-IT': 'it',
    }
    return mapping[locale] || locale
  }

  const getLanguageDisplayName = (
    language: string,
    displayLocale: string,
  ): string => {
    return getDisplayName(language, displayLocale, 'language')
  }

  const handleLanguageSync = (
    currentI18nLanguage: string,
    userPreferenceLocale: string,
    onCompleted: () => void,
  ) => {
    const normalizedCurrent = normalizeLocale(currentI18nLanguage)
    const normalizedPreference = normalizeLocale(userPreferenceLocale)

    if (normalizedCurrent !== normalizedPreference) {
      const loginLanguageInCurrent = getLanguageDisplayName(
        currentI18nLanguage,
        currentI18nLanguage,
      )
      const preferenceLanguageInCurrent = getLanguageDisplayName(
        normalizedPreference,
        currentI18nLanguage,
      )

      const loginLanguageInPreference = getLanguageDisplayName(
        currentI18nLanguage,
        normalizedPreference,
      )
      const preferenceLanguageInPreference = getLanguageDisplayName(
        normalizedPreference,
        normalizedPreference,
      )

      const primaryDescription = t('language_sync.modal.description', {
        loginLanguage: loginLanguageInCurrent,
        preferenceLanguage: preferenceLanguageInCurrent,
      })

      const resources = i18n.getResourceBundle(normalizedPreference, 'common')
      const template = resources?.language_sync?.modal?.description || ''
      const secondaryDescription = template
        .replace('{{loginLanguage}}', loginLanguageInPreference)
        .replace('{{preferenceLanguage}}', preferenceLanguageInPreference)

      const loginButtonText = `${capitalizeFirstLetter(loginLanguageInCurrent)} (${capitalizeFirstLetter(loginLanguageInPreference)})`
      const preferenceButtonText = `${capitalizeFirstLetter(preferenceLanguageInCurrent)} (${capitalizeFirstLetter(preferenceLanguageInPreference)})`

      const customDescription = React.createElement(
        'div',
        {},
        React.createElement('p', {}, primaryDescription),
        React.createElement(
          'p',
          { className: 'italic mt-4' },
          secondaryDescription,
        ),
      )

      handleModal({
        type: 'primary',
        title: t('language_sync.modal.title'),
        description: customDescription,
        cancelText: loginButtonText,
        confirmText: preferenceButtonText,
        onCancel: onCompleted,
        onConfirm: () => {
          i18n.changeLanguage(normalizedPreference)
          onCompleted()
        },
      })
    } else {
      onCompleted()
    }
  }

  const syncLanguageToPreference = (userPreferenceLocale: string) => {
    const normalizedLocale = normalizeLocale(userPreferenceLocale)
    if (i18n.language !== normalizedLocale) {
      i18n.changeLanguage(normalizedLocale)
    }
  }

  return {
    handleLanguageSync,
    syncLanguageToPreference,
    normalizeLocale,
  }
}
